/**
 * i18n Configuration for NGUI
 *
 * Initializes i18next with English and Italian translations.
 * Language is persisted in localStorage and synced with backend.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// English namespaces
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enSettings from './locales/en/settings.json';
import enTasks from './locales/en/tasks.json';
import enFiles from './locales/en/files.json';
import enEmail from './locales/en/email.json';
import enCalendar from './locales/en/calendar.json';
import enPeople from './locales/en/people.json';
import enAdmin from './locales/en/admin.json';
import enVoice from './locales/en/voice.json';
import enBilling from './locales/en/billing.json';
import enSupport from './locales/en/support.json';
import enSteering from './locales/en/steering.json';
import enChat from './locales/en/chat.json';
import enContractors from './locales/en/contractors.json';

// Italian namespaces
import itCommon from './locales/it/common.json';
import itAuth from './locales/it/auth.json';
import itSettings from './locales/it/settings.json';
import itTasks from './locales/it/tasks.json';
import itFiles from './locales/it/files.json';
import itEmail from './locales/it/email.json';
import itCalendar from './locales/it/calendar.json';
import itPeople from './locales/it/people.json';
import itAdmin from './locales/it/admin.json';
import itVoice from './locales/it/voice.json';
import itBilling from './locales/it/billing.json';
import itSupport from './locales/it/support.json';
import itSteering from './locales/it/steering.json';
import itChat from './locales/it/chat.json';
import itContractors from './locales/it/contractors.json';

const LANGUAGE_KEY = 'ngui_ui_language';
const SUPPORTED_LANGUAGES = ['en', 'it'];

/**
 * Detect browser's preferred language from navigator.languages or navigator.language
 * Returns the first supported language found, or 'en' as fallback
 */
function detectBrowserLanguage(): string {
  // Get browser's preferred languages (in order of preference)
  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    // Extract base language code (e.g., 'it-IT' -> 'it')
    const baseLang = lang.split('-')[0].toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(baseLang)) {
      return baseLang;
    }
  }

  return 'en';
}

export function getSavedLanguage(): string {
  // First check for saved preference
  const saved = localStorage.getItem(LANGUAGE_KEY);
  if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
    return saved;
  }

  // If no saved preference, detect from browser
  return detectBrowserLanguage();
}

export function saveLanguage(lang: string): void {
  if (SUPPORTED_LANGUAGES.includes(lang)) {
    localStorage.setItem(LANGUAGE_KEY, lang);
  }
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        auth: enAuth,
        settings: enSettings,
        tasks: enTasks,
        files: enFiles,
        email: enEmail,
        calendar: enCalendar,
        people: enPeople,
        admin: enAdmin,
        voice: enVoice,
        billing: enBilling,
        support: enSupport,
        steering: enSteering,
        chat: enChat,
        contractors: enContractors,
      },
      it: {
        common: itCommon,
        auth: itAuth,
        settings: itSettings,
        tasks: itTasks,
        files: itFiles,
        email: itEmail,
        calendar: itCalendar,
        people: itPeople,
        admin: itAdmin,
        voice: itVoice,
        billing: itBilling,
        support: itSupport,
        steering: itSteering,
        chat: itChat,
        contractors: itContractors,
      },
    },
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'auth', 'settings', 'tasks', 'files', 'email', 'calendar', 'people', 'admin', 'voice', 'billing', 'support', 'steering', 'chat', 'contractors'],
    interpolation: {
      escapeValue: false,
    },
  });

export { SUPPORTED_LANGUAGES, LANGUAGE_KEY };
export default i18n;
